import { useRef, useEffect, KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Loader2, Send, RotateCcw, Square } from 'lucide-react';

interface PromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStopGeneration?: () => void;
  onResetContext?: () => Promise<void>;
  isProcessing: boolean;
  isResetting?: boolean;
  hasMessages: boolean;
  /** Content rendered to the left of the textarea (e.g. mode toggle) */
  leftAddon?: React.ReactNode;
  /** Dynamic placeholder text */
  placeholder?: string;
  className?: string;
}

export function PromptInput({
  value,
  onChange,
  onSubmit,
  onStopGeneration,
  onResetContext,
  isProcessing,
  isResetting,
  hasMessages,
  leftAddon,
  placeholder,
  className,
}: PromptInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className={cn('border-t p-4', className)}>
      {leftAddon && <div className="mb-2">{leftAddon}</div>}
      <div className="flex gap-2">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? 'Describe what you want to build...'}
          disabled={isProcessing || isResetting}
          className="min-h-[80px] resize-none"
        />
        <div className="flex flex-col gap-2">
          {isProcessing ? (
            <Button
              onClick={onStopGeneration}
              variant="destructive"
              size="icon"
              className="flex-1"
              title="Stop generation"
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={onSubmit}
              disabled={!value.trim() || isResetting}
              size="icon"
              className="flex-1"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
          {onResetContext && hasMessages && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  disabled={isProcessing || isResetting}
                  title="Reset conversation context"
                >
                  {isResetting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset Conversation Context</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will clear the conversation history but keep your current UI preview
                    intact. You can continue building from the current state with fresh context.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onResetContext}>Reset Context</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
}
